import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, Truck, CheckCircle2, XCircle, Clock, DollarSign } from "lucide-react";

const STATUS_CFG = {
  pending: { icon: Clock, label: "Awaiting payment", cls: "bg-amber-100 text-amber-800" },
  held: { icon: ShieldCheck, label: "Held in escrow", cls: "bg-accent/15 text-accent" },
  released: { icon: CheckCircle2, label: "Released", cls: "bg-emerald-100 text-emerald-800" },
  refunded: { icon: XCircle, label: "Refunded", cls: "bg-red-100 text-red-700" },
};

function StatusPill({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.pending;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${c.cls}`}>
      <Icon className="w-3.5 h-3.5" />{c.label}
    </span>
  );
}

export default function Escrows() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const endpoint = user.role === "admin" ? "/admin/escrows" : user.role === "supplier" ? "/supplier/escrows" : "/buyer/escrows";
  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [endpoint]);

  // Handle Stripe redirect: ?paid=<session_id>
  useEffect(() => {
    const paid = params.get("paid");
    const cancelled = params.get("cancelled");
    if (paid) {
      api.get(`/escrows/status/${paid}`).then((r) => {
        if (r.data.payment_status === "paid") { toast.success("Deposit received — held in escrow."); load(); }
        else toast.info("Verifying payment…");
        params.delete("paid"); setParams(params, { replace: true });
      }).catch(() => {});
    }
    if (cancelled) {
      toast.info("Payment cancelled.");
      params.delete("cancelled"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const doAction = async (eid, action) => {
    setBusy(`${eid}-${action}`);
    try { await api.post(`/admin/escrows/${eid}/${action}`, { note: null }); toast.success(`Escrow ${action}d`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <DashboardLayout title="Trade Assurance escrow">
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {["pending", "held", "released"].map((s) => {
          const count = items.filter((e) => e.status === s).length;
          const total = items.filter((e) => e.status === s).reduce((sum, e) => sum + (e.amount || 0), 0);
          return (
            <Card key={s} className="card-lift" data-testid={`escrow-stat-${s}`}>
              <CardContent className="pt-6">
                <StatusPill status={s} />
                <div className="mt-3 font-heading text-3xl font-black text-secondary">{count}</div>
                <div className="text-xs text-muted-foreground mt-1">${total.toFixed(2)} total</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader><CardTitle className="font-heading tracking-tight">{items.length} escrow{items.length === 1 ? "" : "s"}</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground" data-testid="escrows-empty">
              <DollarSign className="w-10 h-10 mx-auto text-muted-foreground/50" />
              <div className="mt-2">No escrow payments yet.</div>
              {user.role === "buyer" && <div className="mt-1">Once a supplier quotes you, you can lock in a deposit here.</div>}
            </div>
          ) : (
            <Table data-testid="escrows-table">
              <TableHeader><TableRow>
                <TableHead>Quote</TableHead>
                {user.role !== "buyer" && <TableHead>Buyer</TableHead>}
                {user.role !== "supplier" && <TableHead>Supplier</TableHead>}
                <TableHead>Deposit</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                {user.role === "admin" && <TableHead className="text-right">Action</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} data-testid={`escrow-row-${e.id}`}>
                    <TableCell className="font-mono text-xs">{e.quote_id?.slice(-8)}</TableCell>
                    {user.role !== "buyer" && <TableCell>{e.buyer?.company_name || e.buyer?.name || "—"}</TableCell>}
                    {user.role !== "supplier" && <TableCell>{e.supplier?.company_name || e.supplier?.name || "—"}</TableCell>}
                    <TableCell className="font-semibold text-secondary">${e.amount?.toFixed(2)} <Badge variant="outline" className="ml-1 text-[10px]">{e.deposit_percent}%</Badge></TableCell>
                    <TableCell>${e.total?.toFixed(2)}</TableCell>
                    <TableCell><StatusPill status={e.status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                    {user.role === "admin" && (
                      <TableCell className="text-right space-x-2">
                        {e.status === "held" && (
                          <>
                            <Button size="sm" onClick={() => doAction(e.id, "release")} disabled={busy === `${e.id}-release`} data-testid={`release-${e.id}`}><Truck className="w-3.5 h-3.5 mr-1" />Release</Button>
                            <Button size="sm" variant="outline" onClick={() => doAction(e.id, "refund")} disabled={busy === `${e.id}-refund`} data-testid={`refund-${e.id}`}>Refund</Button>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
